// src/pages/PatientLoginPage.jsx
// Route: /login

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePatient } from '../context/PatientContext';
import previuLogo from '../assets/previu-logo.jpeg';

const API_BASE = process.env.REACT_APP_API_URL
  || 'https://previuagenticplatform-production.up.railway.app/api';

const inputStyle = {
  width: '100%', boxSizing: 'border-box',
  border: '1.5px solid var(--border)',
  borderRadius: 'var(--radius-md)',
  padding: '12px 14px', fontSize: 15,
  fontFamily: 'var(--font-body)',
  color: 'var(--text-primary)',
  background: 'var(--bg-subtle)',
  outline: 'none', transition: 'border-color 0.2s',
};

const primaryBtn = (disabled) => ({
  width: '100%', border: 'none',
  borderRadius: 'var(--radius-md)',
  padding: '13px 18px', fontSize: 15, fontWeight: 600,
  color: 'var(--text-inverse)', cursor: disabled ? 'not-allowed' : 'pointer',
  fontFamily: 'var(--font-body)', marginTop: 8,
  background: disabled ? 'var(--text-muted)' : 'var(--brand-accent)',
  transition: 'background 0.15s',
});

function ErrorMsg({ msg }) {
  if (!msg) return null;
  return <div style={{ fontSize: 13, color: 'var(--red)', marginTop: 8 }}>{msg}</div>;
}

// ── Post-login navigation ────────────────────────────────────────────────────
//
// DEF-046 / TC-P002 — "postLoginRoute not working correctly. All accounts land
// on the same patient dashboard regardless of onboarding state."
//
// The old implementation read the answer out of localStorage:
//
//     const onboardingDone = localStorage.getItem('previu_onboarding_complete');
//
// Onboarding state is a property of the PATIENT, not of the browser. Reading it
// from localStorage means:
//   - a patient logging in on a new device or a cleared browser looks brand new,
//     however much onboarding they have completed
//   - a patient on a shared device inherits whoever used it last
//   - the flags are written by PatientOnboardingPage and HealthProfileForm, so
//     any patient who completed onboarding through a path that does not write
//     them is invisible to this check
// which is why every account landed in the same place.
//
// It also collapsed two of the three states TC-P002 specifies: "skipped" and
// "never started" both routed to /patient/onboarding, when skipped is supposed
// to reach chat with a nudge.
//
// The symptom questionnaire IS the onboarding intake (Constitution §VIII, 20
// questions), and GET /api/patients/{id}/symptoms already reports how many are
// answered. That is the server-side source of truth, so ask it.

/**
 * Resolves the post-login route.
 *
 * ONBOARDING IS SHOWN EXACTLY ONCE — on the first login of a patient who has
 * never seen it. The condition is patients.onboarding_seen_at being null, which
 * the onboarding screen itself stamps when it renders.
 *
 * It used to be inferred from the symptom answer count:
 *
 *     if (answered === 0) return '/patient/onboarding';
 *
 * Answer count is a proxy for "has been onboarded", and a bad one: a patient
 * who reached the screen and skipped it looks identical to one who has never
 * seen it, so they were sent back to a first-run screen on every single login.
 * Before that the check read a localStorage flag, which this file's own history
 * records as wrong for a different reason — onboarding state belongs to the
 * patient, not the browser.
 *
 * The answers that a skipper never gave are now collected by the portal's
 * profile gate (PatientLayout.ProfileCompletionGate), which names what is
 * missing and links to it, instead of by a redirect the patient has already
 * declined once.
 *
 * @returns {Promise<string>} '/patient/onboarding' on a true first login,
 *          '/patient/today' every time after.
 */
export async function resolvePostLoginRoute(patientId) {
  if (!patientId) return '/patient/onboarding';

  try {
    const res = await fetch(`${API_BASE}/patients/${patientId}`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('previu_token')}`,
      },
    });
    if (!res.ok) throw new Error(`patient → ${res.status}`);

    const data = await res.json();
    return data?.onboardingSeenAt ? '/patient/today' : '/patient/onboarding';
  } catch {
    // Fail towards the portal, not towards onboarding. A returning patient
    // seeing a first-run screen because one request failed is the exact defect
    // this replaced; an onboarded patient reaching the portal is harmless, and
    // the profile gate still holds them if their intake is genuinely incomplete.
    return '/patient/today';
  }
}

// A patient route is a same-origin path this app's router handles; anything
// else is a different origin that needs a real
// navigation, not react-router's navigate().
export function goToPostLoginRoute(navigate, route) {
  if (/^https?:\/\//.test(route)) {
    window.location.href = route;
  } else {
    navigate(route);
  }
}

function ChangePasswordScreen({ phone, onDone }) {
  const [pw, setPw]           = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    if (pw.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (pw !== confirm) { setError('Passwords do not match.'); return; }
    setError(''); setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/patient-auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('previu_token')}`,
        },
        body: JSON.stringify({ phone, newPassword: pw }),
      });
      if (!res.ok) { setError('Could not update password. Please try again.'); return; }
      onDone();
    } catch { setError('Something went wrong.'); }
    finally { setLoading(false); }
  }

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-display)', marginBottom: 6 }}>
        Set your password
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 24, lineHeight: 1.6 }}>
        Your account was set up by a navigator. Please set a new password to continue.
      </div>
      <label htmlFor="patient-new-password" style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>New password</label>
      <input id="patient-new-password" type="password" value={pw} onChange={e => setPw(e.target.value)}
        placeholder="Min. 8 characters" style={inputStyle} required
        onFocus={e => (e.target.style.borderColor = 'var(--green)')}
        onBlur={e => (e.target.style.borderColor = 'var(--border)')} />
      <label htmlFor="patient-confirm-password" style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginTop: 14, marginBottom: 6 }}>Confirm password</label>
      <input id="patient-confirm-password" type="password" value={confirm} onChange={e => setConfirm(e.target.value)}
        placeholder="Repeat password" style={inputStyle} required
        onFocus={e => (e.target.style.borderColor = 'var(--green)')}
        onBlur={e => (e.target.style.borderColor = 'var(--border)')} />
      <ErrorMsg msg={error} />
      <button type="submit" style={primaryBtn(loading || pw.length < 8)} disabled={loading || pw.length < 8}>
        {loading ? 'Saving…' : 'Save password & continue →'}
      </button>
    </form>
  );
}

function sendOtpError(status, data) {
  if (data && (data.message || data.error)) return data.message || data.error;
  if (status === 404) return 'Phone number not registered. Please contact your navigator.';
  if (status >= 500) {
    return 'We could not send the code on WhatsApp just now. '
      + 'Please try again, or sign in with your password.';
  }
  return 'Could not send the code. Please try again.';
}

// Canonical Indian mobile: "91" + 10 digits. Accepts "9876543210", "09876543210",
// "919876543210" and "+91 98765 43210". Anything else is returned as bare digits
// (e.g. UAE 971…) for the backend to resolve; null when too short to be a number.
function normalizePhone(raw) {
  let digits = raw.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('0')) digits = digits.slice(1);
  if (digits.length === 10) return `91${digits}`;
  return digits.length < 10 ? null : digits;
}

function OtpTab({ onSuccess }) {
  const [step, setStep]       = useState('phone');
  const [phone, setPhone]     = useState('');
  const [otp, setOtp]         = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [resend, setResend]   = useState(0);

  function startTimer() {
    setResend(30);
    const id = setInterval(() => setResend(t => { if (t <= 1) { clearInterval(id); return 0; } return t - 1; }), 1000);
  }

  async function sendOtp(e) {
    e.preventDefault();
    const cleaned = normalizePhone(phone);
    if (!cleaned) { setError('Please enter a valid phone number.'); return; }
    setError(''); setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/patient-auth/send-otp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: cleaned }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(sendOtpError(res.status, data)); return; }
      setStep('otp'); startTimer();
    } catch { setError('Something went wrong. Please try again.'); }
    finally { setLoading(false); }
  }

  async function verifyOtp(e) {
    e.preventDefault();
    if (otp.length !== 6) { setError('Please enter the 6-digit code.'); return; }
    setError(''); setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/patient-auth/verify-otp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: normalizePhone(phone), otp }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Incorrect code. Please try again.'); return; }
      onSuccess(data);
    } catch { setError('Something went wrong. Please try again.'); }
    finally { setLoading(false); }
  }

  async function resendOtp() {
    if (resend > 0) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/patient-auth/send-otp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: normalizePhone(phone) }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(sendOtpError(res.status, data));
        return;
      }
      setError(''); startTimer();
    } catch { setError('Could not resend. Please try again.'); }
    finally { setLoading(false); }
  }

  if (step === 'phone') return (
    <form onSubmit={sendOtp} noValidate>
      <label htmlFor="patient-otp-phone" style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>Phone number</label>
      <input id="patient-otp-phone" type="tel" value={phone} onChange={e => setPhone(e.target.value)}
        placeholder="e.g. 9876543210" style={inputStyle} autoFocus required
        onFocus={e => (e.target.style.borderColor = 'var(--green)')}
        onBlur={e => (e.target.style.borderColor = 'var(--border)')} />
      <ErrorMsg msg={error} />
      <button type="submit" style={primaryBtn(loading)} disabled={loading}>
        {loading ? 'Sending…' : 'Send OTP on WhatsApp →'}
      </button>
    </form>
  );

  return (
    <form onSubmit={verifyOtp} noValidate>
      <button type="button" onClick={() => { setStep('phone'); setOtp(''); setError(''); }}
        style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 13, cursor: 'pointer', padding: 0, marginBottom: 16 }}>
        ← Back
      </button>
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16, lineHeight: 1.6 }}>
        Code sent to <strong style={{ color: 'var(--text-primary)' }}>{phone}</strong> on WhatsApp.
      </div>
      <label htmlFor="patient-otp-code" style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>6-digit code</label>
      <input id="patient-otp-code" type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6}
        value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
        placeholder="— — — — — —"
        style={{ ...inputStyle, fontSize: 22, letterSpacing: 8, textAlign: 'center' }}
        autoFocus required
        onFocus={e => (e.target.style.borderColor = 'var(--green)')}
        onBlur={e => (e.target.style.borderColor = 'var(--border)')} />
      <ErrorMsg msg={error} />
      <button type="submit" style={primaryBtn(loading || otp.length !== 6)} disabled={loading || otp.length !== 6}>
        {loading ? 'Verifying…' : 'Verify & continue →'}
      </button>
      <div style={{ marginTop: 14, textAlign: 'center', fontSize: 13, color: 'var(--text-muted)' }}>
        Didn't receive it?{' '}
        <button type="button" onClick={resendOtp} disabled={resend > 0}
          style={{ background: 'none', border: 'none', padding: 0, fontSize: 13, fontWeight: 600,
            color: resend > 0 ? 'var(--text-muted)' : 'var(--green)',
            cursor: resend > 0 ? 'default' : 'pointer' }}>
          {resend > 0 ? `Resend in ${resend}s` : 'Resend code'}
        </button>
      </div>
    </form>
  );
}

function PasswordTab({ onSuccess, onUseOtp }) {
  const [phone, setPhone]       = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw]     = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    const cleaned = normalizePhone(phone);
    if (!cleaned) { setError('Please enter a valid phone number.'); return; }
    if (!password) { setError('Please enter your password.'); return; }
    setError(''); setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/patient-auth/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: cleaned, password }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Incorrect phone or password.'); return; }
      onSuccess(data);
    } catch { setError('Something went wrong. Please try again.'); }
    finally { setLoading(false); }
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <label htmlFor="patient-password-phone" style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>Phone number</label>
      <input id="patient-password-phone" type="tel" value={phone} onChange={e => setPhone(e.target.value)}
        placeholder="e.g. 9876543210" style={inputStyle} autoFocus required
        onFocus={e => (e.target.style.borderColor = 'var(--green)')}
        onBlur={e => (e.target.style.borderColor = 'var(--border)')} />

      <label htmlFor="patient-password" style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginTop: 14, marginBottom: 6 }}>Password</label>
      <div style={{ position: 'relative' }}>
        <input id="patient-password" type={showPw ? 'text' : 'password'} value={password}
          onChange={e => setPassword(e.target.value)}
          placeholder="Your password" style={{ ...inputStyle, paddingRight: 44 }} required
          onFocus={e => (e.target.style.borderColor = 'var(--green)')}
          onBlur={e => (e.target.style.borderColor = 'var(--border)')} />
        <button type="button" onClick={() => setShowPw(s => !s)}
          style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
            background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 13 }}>
          {showPw ? 'Hide' : 'Show'}
        </button>
      </div>

      <ErrorMsg msg={error} />
      <button type="submit" style={primaryBtn(loading)} disabled={loading}>
        {loading ? 'Signing in…' : 'Sign in →'}
      </button>

      <div style={{ marginTop: 16, fontSize: 12.5, color: 'var(--text-muted)',
        lineHeight: 1.6, textAlign: 'center' }}>
        No password yet?{' '}
        <button type="button" onClick={onUseOtp}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer',
            color: 'var(--green)', fontWeight: 600, fontSize: 12.5,
            fontFamily: 'inherit', textDecoration: 'underline' }}>
          Sign in with WhatsApp OTP
        </button>
        {' '}— you can set one afterwards under Settings.
      </div>
    </form>
  );
}

export default function PatientLoginPage() {
  const navigate = useNavigate();
  const { refreshPatient } = usePatient();
  const [tab, setTab]       = useState('otp');
  const [screen, setScreen] = useState('login');
  const [pendingPhone, setPendingPhone] = useState('');

  async function handleAuthSuccess(data) {
    localStorage.setItem('previu_token', data.token);
    const nameParts = (data.name || '').trim().split(' ');
    localStorage.setItem('previu_patient', JSON.stringify({
      id:        data.patientId,
      firstName: nameParts[0] || 'Patient',
      lastName:  nameParts.slice(1).join(' ') || '',
      phone:     data.phone,
      email:     data.email || '',
    }));

    refreshPatient();

    if (data.mustChangePassword) {
      setPendingPhone(data.phone);
      setScreen('change-password');
    } else {
      // Onboarding state comes from the server now — see resolvePostLoginRoute
      // (DEF-046). Awaited so the patient lands in one place rather than being
      // redirected out from under themselves.
      goToPostLoginRoute(navigate, await resolvePostLoginRoute(data.patientId));
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, var(--brand-dark) 0%, var(--brand-navy) 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '24px 16px', fontFamily: 'var(--font-body)',
    }}>
      <div style={{
        background: 'var(--bg-surface)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)', padding: '40px 36px',
        width: '100%', maxWidth: 400, boxShadow: 'var(--shadow-md)',
      }}>
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
          <img src={previuLogo} alt="Previu" style={{ height: 140, width: 'auto' }} />
        </div>

        {screen === 'change-password' ? (
          <ChangePasswordScreen
            phone={pendingPhone}
            onDone={async () => {
              let patientId = null;
              try {
                patientId = JSON.parse(localStorage.getItem('previu_patient') || '{}').id || null;
              } catch { /* malformed — resolvePostLoginRoute handles a null id */ }
              goToPostLoginRoute(navigate, await resolvePostLoginRoute(patientId));
            }}
          />
        ) : (
          <>
            <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-display)', marginBottom: 20 }}>
              Welcome back
            </div>

            <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', marginBottom: 24 }}>
              {[{ id: 'otp', label: 'WhatsApp OTP' }, { id: 'password', label: 'Password' }].map(t => (
                <button key={t.id} type="button" onClick={() => setTab(t.id)}
                  style={{
                    flex: 1, background: 'none', border: 'none',
                    borderBottom: tab === t.id ? '2px solid var(--green)' : '2px solid transparent',
                    padding: '10px 0', fontSize: 14, fontWeight: tab === t.id ? 600 : 400,
                    color: tab === t.id ? 'var(--green)' : 'var(--text-muted)',
                    cursor: 'pointer', marginBottom: -1, transition: 'all 0.15s',
                    fontFamily: 'var(--font-body)',
                  }}>
                  {t.label}
                </button>
              ))}
            </div>

            {tab === 'otp'
              ? <OtpTab      onSuccess={handleAuthSuccess} />
              : <PasswordTab onSuccess={handleAuthSuccess} onUseOtp={() => setTab('otp')} />
            }

            <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 10, textAlign: 'center' }}>

              <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                New to Previu?{' '}
                <a href="/register"
                  style={{ color: 'var(--brand-accent)', fontWeight: 600, textDecoration: 'none' }}>
                  Create your account →
                </a>
              </div>

              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                Are you a navigator or doctor?{' '}
                <a href="/nav-login"
                  style={{ color: 'var(--brand-dark)', fontWeight: 600, textDecoration: 'none' }}>
                  Staff login →
                </a>
              </div>

            </div>
          </>
        )}

        <div style={{ marginTop: 28, textAlign: 'center', fontSize: 11, color: 'var(--text-muted)' }}>
          Previu Health · Care navigation made simple
        </div>
      </div>
    </div>
  );
}